<template>
    <div class="team-card">
        <div class="team-card__top">
            <span class="team-greeting">👋 Hi, <b>{{ userName }}</b></span>
            <span class="team-pill">{{ moverIcon }} {{ team }}</span>
        </div>

        <div class="team-card__grid">
            <div class="team-block">
                <p class="team-block__label">👥 Your team</p>
                <div class="team-chips">
                    <span v-for="member in teamMembers" :key="member.name" class="member-chip">{{ member.name }}</span>
                </div>
            </div>

            <div class="team-block">
                <p class="team-block__label"><i class="fas fa-shopping-bag bag-icon"></i> Bags to deliver</p>
                <div v-if="deliveryBagsCount" class="bag-stats">
                    <span class="bag-chip"><b>{{ deliveryBagsCount.normalBags }}</b> Normal</span>
                    <span class="bag-chip bag-chip--dry"><b>{{ deliveryBagsCount.dryBags }}</b> Dry</span>
                </div>
                <progress v-else class="progress is-small is-primary" max="100">15%</progress>
            </div>
        </div>
        <div class="align-center">
            <div v-if="deliveryBagsCount">
                <div v-if="deliveryBagsCount.remainingDeliveries > 0">
                    <!-- Animated delivery journey -->
                    <div class="journey">
                        <div class="journey__line"></div>
                        <div class="journey__fill" :style="{ width: progressPercent + '%' }"></div>
                        <span class="journey__mover" :class="isDriving ? 'is-driving' : 'is-walking'"
                            :style="moverStyle">{{ moverIcon }}</span>
                        <span class="journey__finish">🏁</span>
                    </div>
                    <p class="journey-count">
                        <strong>{{ deliveryBagsCount.completedDeliveries }}</strong> delivered,
                        <strong>{{ deliveryBagsCount.remainingDeliveries }}</strong> to go!
                    </p>
                </div>
                <div v-else class="all-done">
                    <div class="all-done__emoji">🎉</div>
                    <p class="all-done__title">All deliveries done!</p>
                    <p class="all-done__thanks">Thank you for your kindness 😊</p>
                    <div class="all-done__note">
                        <span class="all-done__note-icon"><i class="fas fa-shopping-bag bag-icon"></i></span>
                        <span>Please return the <b>undelivered &amp; empty IKEA bags</b> to an Apexian at the void deck.</span>
                    </div>
                    <OfferHelpBanner />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import OfferHelpBanner from '@/components/volunteer/OfferHelpBanner.vue';

export default {
    components: { OfferHelpBanner },
    props: ['team', 'fdID'],
    created() {
        this.loadTeamMembers();
    },
    watch: {
        team(newTeam, oldTeam) {
            if (newTeam && newTeam !== oldTeam) {
                this.loadTeamMembers();
            }
        }
    },
    methods: {
        async loadTeamMembers() {
            await this.$store.dispatch('volunteer/loadTeamMembers', {
                fdID: this.fdID,
                team: this.team
            });
        }
    },
    computed: {
        teamMembers() {
            return this.$store.getters['volunteer/getTeamMembers'];
        },
        deliveryBagsCount() {
            return this.$store.getters['delivery/getDeliveryBagsCount'];
        },
        userName() {
            const user = this.$store.getters['user/getCurrentUser'];
            return user ? user.name : '';
        },
        isDriving() {
            return (this.team || '').toLowerCase().includes('driv');
        },
        moverIcon() {
            return this.isDriving ? '🚗' : '🚶';
        },
        progressPercent() {
            const count = this.deliveryBagsCount;
            if (!count) {
                return 0;
            }
            const total = count.completedDeliveries + count.remainingDeliveries;
            return total > 0 ? Math.round((count.completedDeliveries / total) * 100) : 0;
        },
        moverStyle() {
            // Keep the icon fully inside the track (it is ~2.2rem wide) as it advances.
            return { left: 'calc((100% - 2.2rem) * ' + (this.progressPercent / 100) + ')' };
        }
    }
}

</script>

<style scoped>
.team-card {
    background: linear-gradient(160deg, #ffffff 0%, #f5f6ff 55%, #eef2ff 100%);
    border: 1px solid #e7e9f5;
    border-radius: 16px;
    padding: 1rem 1.1rem;
    box-shadow: 0 6px 20px -12px rgba(15, 23, 42, 0.25);
}

.team-card__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 0.75rem;
    flex-wrap: wrap;
    margin-bottom: 0.85rem;
}

.team-greeting {
    font-size: 1.05rem;
    color: #374151;
}

.team-greeting b {
    color: #1f2937;
}

.team-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
    color: #4f46e5;
    font-weight: 700;
    font-size: 0.95rem;
    padding: 0.35rem 0.85rem;
    border-radius: 999px;
}

.team-card__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.9rem;
    margin-bottom: 0.4rem;
}

.team-block__label {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #9ca3af;
    font-weight: 700;
    margin-bottom: 0.5rem;
}

/* IKEA bags are blue */
.bag-icon {
    color: #0058a3;
}

.team-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.member-chip {
    background: #f3f4f6;
    color: #374151;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.2rem 0.65rem;
    border-radius: 999px;
}

.bag-stats {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.bag-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    width: fit-content;
    background: #f3f4f6;
    color: #4b5563;
    border-radius: 10px;
    padding: 0.25rem 0.65rem;
    font-size: 0.85rem;
}

.bag-chip b {
    font-size: 1rem;
    color: #111827;
}

.bag-chip--dry {
    background: #ede9fe;
    color: #6d28d9;
}

.bag-chip--dry b {
    color: #6d28d9;
}

.all-done {
    padding: 0.4rem 0 0.2rem;
}

.all-done__emoji {
    font-size: 2.5rem;
    line-height: 1;
    animation: done-pop 0.6s ease-out both;
}

@keyframes done-pop {
    0%   { transform: scale(0); }
    70%  { transform: scale(1.2); }
    100% { transform: scale(1); }
}

.all-done__title {
    font-size: 1.3rem;
    font-weight: 800;
    color: #047857;
    margin-top: 0.35rem;
}

.all-done__thanks {
    color: #4b5563;
    font-size: 0.95rem;
    margin-top: 0.1rem;
}

.all-done__note {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    text-align: left;
    margin-top: 0.9rem;
    padding: 0.7rem 0.85rem;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid #e7e9f5;
    border-radius: 12px;
    font-size: 0.9rem;
    color: #4b5563;
}

.all-done__note b {
    color: #1f2937;
}

.all-done__note-icon {
    font-size: 1.1rem;
    line-height: 1.3;
}

.journey-count {
    color: #4b5563;
    font-size: 0.95rem;
}

.journey-count strong {
    color: #4f46e5;
}

.journey {
    position: relative;
    height: 2.4rem;
    margin: 0.6rem 0 0.4rem;
}

.journey__line {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 4px;
    transform: translateY(-50%);
    border-radius: 2px;
    background: repeating-linear-gradient(90deg, #c7d2fe 0 8px, transparent 8px 16px);
}

.journey__fill {
    position: absolute;
    top: 50%;
    left: 0;
    height: 4px;
    transform: translateY(-50%);
    border-radius: 2px;
    background: #6366f1;
    transition: width 0.8s ease;
}

.journey__mover {
    position: absolute;
    top: 0;
    font-size: 1.5rem;
    line-height: 2.4rem;
    transition: left 0.8s ease;
    will-change: left;
    /* emojis face left by default — flip so they head toward the finish */
    transform: scaleX(-1);
}

.journey__mover.is-walking {
    animation: journey-bob 0.6s ease-in-out infinite;
}

.journey__mover.is-driving {
    animation: journey-vroom 0.5s ease-in-out infinite;
}

.journey__finish {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1.25rem;
}

@keyframes journey-bob {
    0%, 100% { transform: scaleX(-1) translateY(0); }
    50%      { transform: scaleX(-1) translateY(-3px); }
}

@keyframes journey-vroom {
    0%, 100% { transform: scaleX(-1) translateY(0) rotate(0deg); }
    50%      { transform: scaleX(-1) translateY(-1px) rotate(-2deg); }
}

@media (prefers-reduced-motion: reduce) {
    .journey__mover,
    .all-done__emoji { animation: none; }
}
</style>